import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateMessagesOptions,
  BulkCreateMessagesResponse,
  BulkGetByInboxMessagesOptions,
  BulkGetByInboxMessagesResponse,
  CreateMessageStreamResponse,
  ListMessagesOptions,
  ListMessagesResponse,
  MediaAttachmentUploadUrlOptions,
  MediaAttachmentUploadUrlResponse,
  Message,
  bulkCreateMessages as universalBulkCreateMessages,
  bulkGetByInboxMessages as universalBulkGetByInboxMessages,
  createMessageStream as universalCreateMessageStream,
  listMessages as universalListMessages,
  mediaAttachmentUploadUrl as universalMediaAttachmentUploadUrl,
} from './innovation-widget-v1-message-messages.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ai-site-chat' };

export function bulkCreateMessages(
  httpClient: HttpClient
): BulkCreateMessagesSignature {
  return (
    options?: NonNullablePaths<
      BulkCreateMessagesOptions,
      `messages.${number}.messageType`,
      4
    >
  ) =>
    universalBulkCreateMessages(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateMessagesSignature {
  /**
   * Bulk creates messages.
   *
   * This method must be called using the site visitor or site member identity ([SDK](https://dev.wix.com/docs/sdk/articles/get-started/about-identities#site-member) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#site-member)) to identify the site visitor that is creating the messages.
   */
  (
    options?: NonNullablePaths<
      BulkCreateMessagesOptions,
      `messages.${number}.messageType`,
      4
    >
  ): Promise<
    NonNullablePaths<
      BulkCreateMessagesResponse,
      | `messages`
      | `messages.${number}.sender`
      | `messages.${number}.messageType`,
      4
    >
  >;
}

/** @internal */
export function createMessageStream(
  httpClient: HttpClient
): CreateMessageStreamSignature {
  return (message: Message) =>
    universalCreateMessageStream(
      message,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateMessageStreamSignature {
  /**
   * Creates a single message and streams the assistant's response back.
   *
   * First frame: CreateMessageStreamResponse.response.question - the persisted input Message with server-assigned id and timestamps.
   * Intermediate frames: CreateMessageStreamResponse.response.message - text chunks of the assistant's answer as they are generated.
   * Final frame before completion: CreateMessageStreamResponse.response.answer - the persisted answer Message.
   * @param - Message to create and stream a response for.
   */
  (message: Message): Promise<
    NonNullablePaths<
      CreateMessageStreamResponse,
      | `question.sender`
      | `question.messageType`
      | `question.body.additionalData`
      | `question.visitorOptions`
      | `question.assistantActions`
      | `question.visitorOptionsFields`
      | `answer.sender`
      | `answer.messageType`
      | `answer.body.additionalData`
      | `answer.visitorOptions`
      | `answer.assistantActions`
      | `answer.visitorOptionsFields`
      | `message`,
      4
    >
  >;
}

export function listMessages(httpClient: HttpClient): ListMessagesSignature {
  return (options?: ListMessagesOptions) =>
    universalListMessages(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMessagesSignature {
  /**
   * Retrieves a list of messages.
   *
   * Up to 100 Messages can be returned per request.
   *
   * This method must be called using the site visitor or site member identity ([SDK](https://dev.wix.com/docs/sdk/articles/get-started/about-identities#site-member) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#site-member)) to identify the site visitor that is retrieving the messages.
   * @param - Additional options for listing messages.
   */
  (options?: ListMessagesOptions): Promise<
    NonNullablePaths<
      ListMessagesResponse,
      | `messages`
      | `messages.${number}.sender`
      | `messages.${number}.messageType`,
      4
    >
  >;
}

export function mediaAttachmentUploadUrl(
  httpClient: HttpClient
): MediaAttachmentUploadUrlSignature {
  return (mimeType: string, options?: MediaAttachmentUploadUrlOptions) =>
    universalMediaAttachmentUploadUrl(
      mimeType,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface MediaAttachmentUploadUrlSignature {
  /**
   * Allows site visitor to create upload url to use for uploading a file.
   * @param - File type e.g. "image/png".
   */
  (mimeType: string, options?: MediaAttachmentUploadUrlOptions): Promise<
    NonNullablePaths<MediaAttachmentUploadUrlResponse, `uploadUrl`, 2>
  >;
}

export function bulkGetByInboxMessages(
  httpClient: HttpClient
): BulkGetByInboxMessagesSignature {
  return (
    inboxConversationId: string,
    options?: BulkGetByInboxMessagesOptions
  ) =>
    universalBulkGetByInboxMessages(
      inboxConversationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkGetByInboxMessagesSignature {
  /**
   * Retrieves messages by inbox conversation ID.
   *
   * Up to 100 Messages can be returned per request.
   * @param - conversationId of the conversation in inbox.
   *
   * Can be found in [Message.conversationId](https://dev.wix.com/docs/api-reference/crm/communication/inbox/messages/message-object)
   */
  (
    inboxConversationId: string,
    options?: BulkGetByInboxMessagesOptions
  ): Promise<
    NonNullablePaths<
      BulkGetByInboxMessagesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.message.sender`
      | `results.${number}.message.messageType`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AllocationResultType,
  ApplicationError,
  Asset,
  Body,
  BulkActionMetadata,
  BulkCreateMessagesOptions,
  BulkCreateMessagesRequest,
  BulkCreateMessagesResponse,
  BulkGetByInboxMessagesOptions,
  BulkGetByInboxMessagesRequest,
  BulkGetByInboxMessagesResponse,
  BulkGetByInboxMessagesResult,
  CancelCreateInsightsRequest,
  CancelCreateInsightsResponse,
  ChatHandler,
  CommunicationsChannelsMessageReceived,
  CreateMessageStreamRequest,
  CreateMessageStreamResponse,
  CreateMessageStreamResponseResponseOneOf,
  CursorPaging,
  Cursors,
  DeleteContext,
  DeleteStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  ListMessagesOptions,
  ListMessagesRequest,
  ListMessagesResponse,
  MediaAttachmentUploadUrlOptions,
  MediaAttachmentUploadUrlRequest,
  MediaAttachmentUploadUrlResponse,
  Message,
  MessageEnvelope,
  MessageType,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  PagingMetadataV2,
  PicassoAssigned,
  PicassoUnassigned,
  RestoreInfo,
  ScheduleCreateInsightsRequest,
  ScheduleCreateInsightsResponse,
  Sender,
  ServiceProvisioned,
  ServiceRemoved,
  SetupRequest,
  SetupResponse,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  TeardownRequest,
  TeardownResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  VisitorMappedToContact,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
} from './innovation-widget-v1-message-messages.universal.js';
